// react
import styled from 'styled-components';
import { useMemo } from 'react';

// components
import { CareHomeCard } from '@/components/molecules';
import { Card, Label, Title } from '@/components/atoms';
import { CardContainer } from '@/components/templates';

// hooks
import { useWindowSizeBreakpoints } from '@/hooks';

// models
import {
  CareHomeAddress,
  CareHomeProviderData,
  CareHomeShortlist,
  CareHomeShortlistData,
} from '@karehero/models';

export const CareHomeOverview = ({
  shortlist,
  shortlistData,
  onMarkSecured,
}: CareHomeOverviewProps) => {
  // hooks
  const { isLg, isXl } = useWindowSizeBreakpoints();

  // memos
  const formatAddress = useMemo(
    () =>
      (address: CareHomeAddress): string => {
        const parts = [
          address.streetAddress1,
          address.city,
          address.district,
          address.county,
          address.region,
          address.country,
          address.postcode,
        ].filter(Boolean);
        return parts.join(', ');
      },
    [],
  );

  return (
    <Wrapper>
      <Header>
        <Title isNoMargin size={'sm'}>
          Your Shortlist
        </Title>
        {shortlistData.items && (
          <StyledCard
            color={'yellow'}
            isBordered={false}
            elevation={0}
            isPaddingSm
          >
            <StyledLabel
              id='shortlist-label'
              text='Your Care Expert has chosen the below care services to match your care needs.'
            />
          </StyledCard>
        )}
      </Header>
      <CardContainer maxColumns={isXl ? 3 : isLg ? 2 : 1}>
        {shortlistData.items && shortlistData.items.length > 0 ? (
          shortlistData.items.map((item: CareHomeProviderData) => (
            <CareHomeCard
              key={item.regulatoryID}
              regulatoryID={item.regulatoryID}
              headerImageUrl={item.photos?.medium}
              name={item.name}
              address={
                item.address
                  ? formatAddress(item.address)
                  : 'Address not available'
              }
              rating={
                item.rating ? item.rating.overallRating : 'No rating available'
              }
              phone={item.enquiryPhone}
              available={item.availability}
              notes={item.notes}
              isSecured={item.regulatoryID === shortlist.securedRegulatoryID}
              onMarkSecured={() =>
                item.regulatoryID && onMarkSecured?.(item.regulatoryID)
              }
            />
          ))
        ) : (
          <StyledText>No care homes available in your shortlist.</StyledText>
        )}
      </CardContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${(props) => props.theme.spacing[8]};
  padding-left: ${(props) => props.theme.spacing[4]};
  padding-right: ${(props) => props.theme.spacing[4]};
  overflow-x: visible;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[16]};
  flex-wrap: wrap;
  justify-content: center;
  padding-bottom: ${(props) => props.theme.spacing[8]};
`;

const StyledText = styled.div`
  font: ${(props) => props.theme.typography.body.md};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const StyledLabel = styled(Label)`
  font: ${(props) => props.theme.typography.body.md.medium};
`;

const StyledCard = styled(Card)`
  margin: ${(props) => props.theme.spacing[24]} 0;
`;

interface CareHomeOverviewProps {
  shortlist: CareHomeShortlist;
  shortlistData: CareHomeShortlistData;
  onMarkSecured?: (regulatoryID: string) => void;
}

CareHomeOverview.displayName = 'CareHomeOverview';

export default CareHomeOverview;
